import {
  getApiAccountsV1UserRetrieveQueryKey,
  useApiAccountsV1SignoutCreate,
  useApiAccountsV1UserPartialUpdate,
  useApiNotificationsV1PreferencesRetrieve,
  useCurrentUser,
  useQueryClient,
} from 'api';
import { NATIVE_LANGUAGE_DISPLAY_NAMES, SUPPORTED_I18N_LANGUAGES, useTranslation } from 'i18n';
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from 'ui';
import { Check, Logout, PersonOutlined, Settings } from 'ui/icons';

import { useUpdateNotificationPreference } from '../../hooks/useUpdateNotificationPreference/useUpdateNotificationPreference';

export const UserMenu = () => {
  const { t, language: currentLanguage } = useTranslation('features');
  const { data: user, isLoading } = useCurrentUser();
  const { data: notificationPreference, isLoading: isNotificationPreferenceLoading } =
    useApiNotificationsV1PreferencesRetrieve();
  const queryClient = useQueryClient();
  const { mutate: logOut } = useApiAccountsV1SignoutCreate({
    mutation: {
      onSuccess: () => {
        window.location.href = '/accounts/auth/sign-in/';
      },
    },
  });
  const { mutate: updateLanguage } = useApiAccountsV1UserPartialUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getApiAccountsV1UserRetrieveQueryKey() });
      },
    },
  });
  const updateNotificationPreference = useUpdateNotificationPreference();

  const isQualityDigestEnabled = notificationPreference?.quality_digest_enabled ?? false;
  const isNotificationPreferenceToggleDisabled = isNotificationPreferenceLoading;

  const toggleQualityDigest = (checked: boolean) => {
    updateNotificationPreference({
      data: checked
        ? {
            email_enabled: true,
            quality_digest_enabled: true,
          }
        : {
            quality_digest_enabled: false,
          },
    });
  };

  return (
    <Menu
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      disableScrollLock
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      equalMenuWidth={false}
      button={
        <IconButton size="small" color="default">
          <PersonOutlined />
        </IconButton>
      }
    >
      <Stack
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.border.main}`,
          px: 2,
          py: 1,
        })}
      >
        {isLoading || isNotificationPreferenceLoading ? (
          <>
            <Skeleton variant="text" />
            <Skeleton variant="text" />
          </>
        ) : (
          <>
            <Typography variant="body1">{user?.full_name}</Typography>
            <Typography variant="body2" color="secondary">
              {user?.company?.trade_company.name}
            </Typography>
          </>
        )}
      </Stack>
      <MenuItem component="a" href="/accounts/manage_company/">
        <ListItemIcon>
          <Settings fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('navigation.userMenu.manageCompany')}</ListItemText>
      </MenuItem>

      <Stack sx={{ borderTop: (theme) => `1px solid ${theme.palette.border.main}`, px: 2, py: 1 }}>
        <Typography variant="caption" color="secondary">
          {t('navigation.userMenu.notifications')}
        </Typography>
      </Stack>
      <MenuItem
        disabled={isNotificationPreferenceToggleDisabled}
        onClick={() => {
          if (!isNotificationPreferenceToggleDisabled) {
            toggleQualityDigest(!isQualityDigestEnabled);
          }
        }}
      >
        <Stack
          onClick={(event) => {
            event.stopPropagation();
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
        >
          <Switch
            checked={isQualityDigestEnabled}
            disabled={isNotificationPreferenceToggleDisabled}
            label={t('navigation.userMenu.qualityDigest')}
            onChange={(event) => {
              toggleQualityDigest(event.target.checked);
            }}
          />
        </Stack>
      </MenuItem>
      <Stack sx={{ borderTop: (theme) => `1px solid ${theme.palette.border.main}`, px: 2, py: 1 }}>
        <Typography variant="caption" color="secondary">
          {t('navigation.userMenu.language')}
        </Typography>
      </Stack>
      {SUPPORTED_I18N_LANGUAGES.map((lang) => (
        <MenuItem
          key={lang}
          selected={currentLanguage === lang}
          onClick={() => {
            if (currentLanguage !== lang) {
              updateLanguage({ data: { language: lang } });
            }
          }}
        >
          <ListItemIcon>{currentLanguage === lang ? <Check fontSize="small" /> : null}</ListItemIcon>
          <ListItemText>{NATIVE_LANGUAGE_DISPLAY_NAMES[lang]}</ListItemText>
        </MenuItem>
      ))}
      <Divider />
      <MenuItem
        onClick={() => {
          logOut();
        }}
      >
        <ListItemIcon>
          <Logout />
        </ListItemIcon>
        <ListItemText>{t('navigation.userMenu.logOut')}</ListItemText>
      </MenuItem>
    </Menu>
  );
};
